import { buttonStyle, ICON_SLOT } from 'components/buttons/buttonStyle';
import { Loader2Icon, SwapHoriz as SwapHorizIcon } from 'components/icons';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput.tsx';
import Loader from 'components/Loader.tsx';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import { FormattedDate } from 'components/misc/FormattedDate.tsx';
import { ModalFooter } from 'components/modals/ModalFooter.tsx';
import PartyName from 'components/party/PartyName';
import { SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar.tsx';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes.tsx';
import { useListTableFilter } from 'components/tables/list-table/useListTableFilter.ts';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tooltip } from 'components/ui/tooltip';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetBankTransferConversionRateQuery, useUpdateBankTransferMutation } from 'store/api/bank-transfers.ts';
import { useGetInvoicesWithFiltersQuery, useLazyGetInvoicesWithFiltersQuery } from 'store/api/invoices.ts';
import {
    BankTransactionConversionRate,
    BankTransactionDetails,
    BankTransactionUpdateDto,
    BankTransferInvoicePayment,
} from 'types/bank-transfers.ts';
import { Employee } from 'types/employees.ts';
import { InvoiceDataListItem, InvoicePayment, InvoiceType } from 'types/invoices.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import {
    formatConversionRate,
    getRowConversion,
    isAmountInTransactionCurrency,
    isUsableRate,
    switchAmountCurrency,
    toAmountInInvoiceCurrency,
    toAmountInTransactionCurrency,
    toInvoicePaymentUpdateDto,
} from './bankTransferPaymentAmounts.ts';

// The cells of both tables: 8px of padding, 16px at either end of a row, a divider under every row but the last.
const CELL_CLASS =
    'border-border text-body1 border-b p-2 whitespace-normal tabular-nums first:pl-4 last:pr-4 [tr:last-child>&]:border-b-0';
const HEAD_CLASS =
    'border-border h-auto border-b p-2 text-xs leading-5 font-medium whitespace-nowrap first:pl-4 last:pr-4';

interface BankTransferConnectInvoicesProps {
    bankTransfer: BankTransactionDetails;
    onBankTransferUpdated: (details: BankTransactionDetails) => void;
    onClose: () => void;
    currentEmployee: Employee | undefined;
}

const BankTransferConnectInvoices: React.FC<BankTransferConnectInvoicesProps> = ({
    bankTransfer: initialBankTransfer,
    onBankTransferUpdated,
    onClose,
    currentEmployee,
}) => {
    const employeeCompanyCountryId = useMemo(
        () => currentEmployee?.latestEmployment?.companyCountryId,
        [currentEmployee]
    );
    const canUpdateAllBankTransactions = useHasPermissions(['UPDATE.BANK_TRANSACTIONS.ALL']);
    const canUpdateMyCompanyBankTransactions = useHasPermissions(['UPDATE.BANK_TRANSACTIONS.MY_COMPANY']);

    const mapInvoiceDataListItemToBankTransferInvoicePayment: (
        invoice: InvoiceDataListItem
    ) => BankTransferInvoicePayment = (invoice: InvoiceDataListItem) => {
        return {
            invoiceId: invoice.id,
            companyCountryId: invoice.invoicedByOffice.companyCountryId,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: invoice.invoiceDate,
            isConnected: false,
            clientPartyName: invoice.clientParty.name,
            outstandingBalanceInPaymentCurrency: invoice.outstandingBalanceInPaymentCurrency,
            amount:
                invoice.paymentCurrencyTypeCode === initialBankTransfer.entryAmountCurrencyTypeCode
                    ? String(
                          Math.min(
                              parseFloat(invoice.outstandingBalanceInPaymentCurrency),
                              initialBankTransfer.entryAmount
                          )
                      )
                    : '0',
            amountCurrencyTypeCode: invoice.paymentCurrencyTypeCode,
            invoicePaymentCurrencyTypeCode: invoice.paymentCurrencyTypeCode,
            readonly: invoice.invoiceTypeCode === InvoiceType.CREDIT || !!invoice.creditInvoices?.length,
        };
    };

    const mapInvoicePaymentToBankTransferInvoicePayment: (invoice: InvoicePayment) => BankTransferInvoicePayment = (
        invoicePayment: InvoicePayment
    ) => {
        return {
            invoicePaymentId: invoicePayment.id,
            invoiceId: invoicePayment.invoice.id,
            companyCountryId: invoicePayment.invoice.invoicedByOffice.companyCountryId,
            isConnected: true,
            invoiceNumber: invoicePayment.invoice.invoiceNumber,
            invoiceDate: invoicePayment.invoice.invoiceDate,
            clientPartyName: invoicePayment.invoice.clientParty.name,
            outstandingBalanceInPaymentCurrency: invoicePayment.invoice.outstandingBalanceInPaymentCurrency,
            amount: String(invoicePayment.amount),
            amountCurrencyTypeCode: invoicePayment.invoice.paymentCurrencyTypeCode,
            savedAmount: String(invoicePayment.amount),
            savedAmountInTransactionCurrency: invoicePayment.amountInTransactionCurrency,
            invoicePaymentCurrencyTypeCode: invoicePayment.invoice.paymentCurrencyTypeCode,
            conversionRate: invoicePayment.transactionCurrencyConversionRate,
            conversionDate: invoicePayment.transactionCurrencyConversionDate,
            readonly:
                invoicePayment.invoice.invoiceTypeCode === InvoiceType.CREDIT ||
                invoicePayment.invoice.hasCreditInvoice,
        };
    };

    const [searchInvoices, { data: searchedInvoices, isFetching: isLoadingSearchedInvoices, isUninitialized }] =
        useLazyGetInvoicesWithFiltersQuery();

    const [bankTransferInvoicePayments, setBankTransferInvoicePayments] = useState<BankTransferInvoicePayment[]>(() =>
        initialBankTransfer.invoicePayments.map((invoicePayment) =>
            mapInvoicePaymentToBankTransferInvoicePayment(invoicePayment)
        )
    );
    const exludeInvoicesFromSuggestionsFilter =
        bankTransferInvoicePayments.length > 0
            ? `;id=out=(${bankTransferInvoicePayments.map((bankTransferInvoicePayment) => bankTransferInvoicePayment.invoiceId).join(',')})`
            : '';

    const { data: suggestedInvoices, isLoading: isLoadingSuggestedInvoices } = useGetInvoicesWithFiltersQuery({
        pageSize: 2147483647,
        page: 0,
        filters: `id=isSuggestableInvoice=${initialBankTransfer.id}${exludeInvoicesFromSuggestionsFilter}`,
    });

    const [isSearchResultsDisplayed, setIsSearchResultsDisplayed] = useState(false);
    const searchAreaRef = useRef<HTMLDivElement>(null);

    // The results close on a press anywhere outside the search box and the results under it.
    useEffect(() => {
        if (!isSearchResultsDisplayed) return;
        const closeOnOutsidePress = (event: PointerEvent) => {
            if (!searchAreaRef.current?.contains(event.target as Node)) {
                setIsSearchResultsDisplayed(false);
            }
        };
        document.addEventListener('pointerdown', closeOnOutsidePress);
        return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
    }, [isSearchResultsDisplayed]);

    useEffect(() => {
        if (suggestedInvoices?.content?.length && suggestedInvoices?.content?.length > 0) {
            setBankTransferInvoicePayments([
                ...suggestedInvoices.content.map((suggestedInvoice) =>
                    mapInvoiceDataListItemToBankTransferInvoicePayment(suggestedInvoice)
                ),
                ...initialBankTransfer.invoicePayments.map((invoicePayment) =>
                    mapInvoicePaymentToBankTransferInvoicePayment(invoicePayment)
                ),
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestedInvoices?.content]);

    const searchInvoicesFilterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [
                { selector: 'remainingAmountInPaymentCurrency', comparator: '=lknumeric=' },
                { selector: 'invoiceNumber' },
                { selector: 'clientParty.name' },
            ],
            passiveFilter: `invoiceStatusTypeCode=in=(DRAFT,REVIEWED,APPROVED,FINALIZED,SENT,PAID);invoiceTypeCode=out=('CREDIT','CORRECTION');remainingAmountInPaymentCurrency>0${exludeInvoicesFromSuggestionsFilter}`,
        }),
        [exludeInvoicesFromSuggestionsFilter]
    );

    const { searchBarProps } = useListTableFilter<InvoiceDataListItem>({
        fetchData: searchInvoices,
        data: searchedInvoices,
        filterConfig: searchInvoicesFilterConfig,
        disableInitialFetch: true,
        disableUrlState: true,
    });

    const handleSearchDebounced = useDebouncedCallback(
        (value = '') => {
            searchBarProps.handleSearch(value);
        },
        isUninitialized ? 0 : 600
    );

    const [updateBankTransfer] = useUpdateBankTransferMutation();
    const [fetchConversionRate] = useLazyGetBankTransferConversionRateQuery();
    const [conversionRates, setConversionRates] = useState<Record<number, BankTransactionConversionRate>>({});

    // The rate is taken on each invoice's own fx date, so it is fetched per invoice rather than per currency.
    const invoiceIdsMissingRate = useMemo(
        () =>
            bankTransferInvoicePayments
                .filter(
                    (payment) =>
                        payment.invoicePaymentCurrencyTypeCode !== initialBankTransfer.entryAmountCurrencyTypeCode &&
                        !isUsableRate(payment.conversionRate) &&
                        !conversionRates[payment.invoiceId]
                )
                .map((payment) => payment.invoiceId),
        [bankTransferInvoicePayments, conversionRates, initialBankTransfer.entryAmountCurrencyTypeCode]
    );

    useEffect(() => {
        invoiceIdsMissingRate.forEach((invoiceId) => {
            fetchConversionRate({ id: initialBankTransfer.id, invoiceId }, true)
                .unwrap()
                .then((rate) => setConversionRates((prev) => ({ ...prev, [invoiceId]: rate })))
                .catch((error) => {
                    console.error(error);
                    logError(error);
                });
        });
    }, [invoiceIdsMissingRate, fetchConversionRate, initialBankTransfer.id]);

    const { handleSubmit, control, setValue } = useForm<{ bankTransferInvoicePayments: BankTransferInvoicePayment[] }>({
        defaultValues: { bankTransferInvoicePayments },
        values: { bankTransferInvoicePayments },
    });

    const [bankTransferInvoicePaymentsWatched] = useWatch({ control, name: ['bankTransferInvoicePayments'] });
    const allChecked = useMemo(
        () =>
            !!bankTransferInvoicePaymentsWatched?.length &&
            bankTransferInvoicePaymentsWatched.every((el) => el.isConnected),
        [bankTransferInvoicePaymentsWatched]
    );
    const allTransfersReadOnly = useMemo(
        () =>
            !!bankTransferInvoicePaymentsWatched?.length &&
            bankTransferInvoicePaymentsWatched.every((el) => el.readonly),
        [bankTransferInvoicePaymentsWatched]
    );
    const hasMyCompanyTransactions = useMemo(
        () => bankTransferInvoicePaymentsWatched?.some((el) => employeeCompanyCountryId === el.companyCountryId),
        [bankTransferInvoicePaymentsWatched, employeeCompanyCountryId]
    );

    const connectedInvoicePaymentAmountSum = useMemo(
        () =>
            Math.round(
                (bankTransferInvoicePaymentsWatched || [])
                    .filter((item) => item.isConnected && item.amount && !isNaN(Number(item.amount)))
                    .reduce((acc, item) => {
                        const conversion = getRowConversion(
                            item,
                            initialBankTransfer.entryAmountCurrencyTypeCode,
                            conversionRates
                        );
                        return acc + (conversion ? toAmountInTransactionCurrency(item, conversion.rate) : NaN);
                    }, 0) * 100
            ) / 100,
        [bankTransferInvoicePaymentsWatched, conversionRates, initialBankTransfer.entryAmountCurrencyTypeCode]
    );

    const disableSave = useMemo(
        () =>
            isNaN(connectedInvoicePaymentAmountSum) ||
            initialBankTransfer.entryAmount - (initialBankTransfer.amountNoninvoice ?? 0) <
                connectedInvoicePaymentAmountSum,
        [connectedInvoicePaymentAmountSum, initialBankTransfer]
    );

    const handleOnChange = async (formData: { bankTransferInvoicePayments: BankTransferInvoicePayment[] }) => {
        const dto: BankTransactionUpdateDto = {
            id: initialBankTransfer.id,
            amountNoninvoice: initialBankTransfer.amountNoninvoice,
            noninvoiceNotes: initialBankTransfer.noninvoiceNotes,
            isNonInvoiceTransaction: initialBankTransfer.isNonInvoiceTransaction,
            invoicePayments: formData.bankTransferInvoicePayments
                .filter((bankTransferInvoicePayment) => bankTransferInvoicePayment.isConnected)
                .map(toInvoicePaymentUpdateDto),
        };
        await updateBankTransfer(dto)
            .unwrap()
            .then((resp) => {
                onBankTransferUpdated(resp);
                onClose();
            })
            .catch((error) => {
                console.error(error);
                logError(error);
            });
    };

    const onSave = async () => {
        await handleSubmit((data) => handleOnChange(data))();
    };

    const { fields } = useFieldArray({
        control,
        name: 'bankTransferInvoicePayments',
    });

    if (isLoadingSuggestedInvoices) {
        return <Loader />;
    }

    const canSelectAll =
        !allTransfersReadOnly &&
        (canUpdateAllBankTransactions || (canUpdateMyCompanyBankTransactions && hasMyCompanyTransactions));

    return (
        <>
            <div
                className={cn(
                    'h-full overflow-y-auto max-sm:px-4 max-sm:py-6',
                    isSearchResultsDisplayed && 'min-h-[444px]'
                )}
            >
                <p className="text-body1 mb-2 font-semibold">
                    <FormattedMessage id="bank-transfers.suggested-and-connected-invoices" />
                </p>
                <div className="flex flex-col">
                    <div ref={searchAreaRef}>
                        <SearchBar
                            value={searchBarProps.searchTextState}
                            onClick={() => {
                                if (isUninitialized) {
                                    handleSearchDebounced(searchBarProps.searchTextState);
                                }
                                setIsSearchResultsDisplayed(true);
                            }}
                            onChange={(event) => {
                                const value = event.target.value;
                                searchBarProps.handleSearchBarChange(value);
                                handleSearchDebounced(value);
                            }}
                            className="mb-2"
                            disabled={false}
                        />
                        {isSearchResultsDisplayed ? (
                            <div className="relative">
                                {/* On the dropdown tier, like any list opened from a field; the content
                                    area it scrolls in keeps it under the app header. */}
                                <table className="bg-grey-100 shadow-card-raised absolute z-(--z-dropdown) w-full rounded-[4px] text-left text-sm">
                                    <TableBody>
                                        {isLoadingSearchedInvoices ? (
                                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                <TableCell className={cn(CELL_CLASS, 'text-center')}>
                                                    <Loader2Icon className="text-primary inline-block size-5 animate-spin" />
                                                </TableCell>
                                            </TableRow>
                                        ) : searchedInvoices?.content !== undefined &&
                                          searchedInvoices.content.length > 0 ? (
                                            searchedInvoices.content
                                                .filter(
                                                    (searchedInvoice) =>
                                                        Number(searchedInvoice.outstandingBalanceInPaymentCurrency) >
                                                            0 &&
                                                        !bankTransferInvoicePayments
                                                            .map((it) => it.invoiceId)
                                                            .includes(searchedInvoice.id)
                                                )
                                                .sort((a, b) => {
                                                    const aIsFromEmployeeOffice =
                                                        employeeCompanyCountryId ===
                                                        a.invoicedByOffice.companyCountryId;
                                                    const bIsFromEmployeeOffice =
                                                        employeeCompanyCountryId ===
                                                        b.invoicedByOffice.companyCountryId;
                                                    if (aIsFromEmployeeOffice && !bIsFromEmployeeOffice) return -1;
                                                    if (!aIsFromEmployeeOffice && bIsFromEmployeeOffice) return 1;
                                                    return 0;
                                                })
                                                .map((searchedInvoice) => (
                                                    <TableRow
                                                        key={searchedInvoice.id}
                                                        className={SIMPLE_TABLE_ROW_CLASS}
                                                    >
                                                        <TableCell className={CELL_CLASS}>
                                                            {searchedInvoice.invoiceNumber}
                                                        </TableCell>
                                                        <TableCell className={CELL_CLASS}>
                                                            {searchedInvoice.invoiceDate ? (
                                                                <DateDefaultNowrap
                                                                    dateValue={searchedInvoice.invoiceDate}
                                                                />
                                                            ) : (
                                                                ''
                                                            )}
                                                        </TableCell>
                                                        <TableCell className={CELL_CLASS}>
                                                            <PartyName
                                                                party={searchedInvoice.clientParty}
                                                                variant="readonly-other"
                                                            />
                                                        </TableCell>
                                                        <TableCell className={CELL_CLASS}>
                                                            <div className="flex flex-row gap-1">
                                                                <FormattedMessage id="bank-transfers.remaining" />
                                                                <MoneyDisplay
                                                                    amount={
                                                                        searchedInvoice.outstandingBalanceInPaymentCurrency
                                                                    }
                                                                    currency={searchedInvoice.paymentCurrencyTypeCode}
                                                                />
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className={CELL_CLASS}>
                                                            <Button
                                                                {...buttonStyle({
                                                                    variant: 'outlined',
                                                                    size: 'extraSmall',
                                                                })}
                                                                disabled={
                                                                    !(
                                                                        canUpdateAllBankTransactions ||
                                                                        (canUpdateMyCompanyBankTransactions &&
                                                                            employeeCompanyCountryId ===
                                                                                searchedInvoice.invoicedByOffice
                                                                                    .companyCountryId)
                                                                    )
                                                                }
                                                                onClick={() => {
                                                                    setBankTransferInvoicePayments([
                                                                        mapInvoiceDataListItemToBankTransferInvoicePayment(
                                                                            searchedInvoice
                                                                        ),
                                                                        ...bankTransferInvoicePayments.map(
                                                                            (el) =>
                                                                                bankTransferInvoicePaymentsWatched.find(
                                                                                    (p) => p.invoiceId === el.invoiceId
                                                                                ) ?? el
                                                                        ),
                                                                    ]);
                                                                    setIsSearchResultsDisplayed(false);
                                                                }}
                                                            >
                                                                <FormattedMessage id="bank-transfers.add" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                        ) : (
                                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                {!isUninitialized ? (
                                                    <TableCell className={cn(CELL_CLASS, 'text-center')}>
                                                        <FormattedMessage id="invoices.no-invoices-found" />
                                                    </TableCell>
                                                ) : null}
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                    <table className="border-divider w-full rounded-[4px] border text-left text-sm">
                        <TableHeader className="bg-grey-50 [&_tr]:border-b-0">
                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableHead className={HEAD_CLASS} colSpan={2}>
                                    {/* min-h-8 sets the header row's height; the 16px checkbox alone would leave it short. */}
                                    <span className="flex min-h-8 items-center gap-2">
                                        <Checkbox
                                            variant="secondary"
                                            aria-label={t('forms.select-all')}
                                            onCheckedChange={(checked) => {
                                                const updatedPayments = bankTransferInvoicePaymentsWatched.map(
                                                    (el) => ({
                                                        ...el,
                                                        isConnected:
                                                            !el.readonly &&
                                                            (canUpdateAllBankTransactions ||
                                                                (canUpdateMyCompanyBankTransactions &&
                                                                    employeeCompanyCountryId === el.companyCountryId))
                                                                ? checked
                                                                : el.isConnected,
                                                    })
                                                );
                                                setValue('bankTransferInvoicePayments', updatedPayments);
                                            }}
                                            checked={allChecked}
                                            disabled={!canSelectAll}
                                        />
                                        <FormattedMessage id="invoices.invoice-number" />
                                    </span>
                                </TableHead>
                                <TableHead className={HEAD_CLASS}>
                                    <FormattedMessage id="invoices.invoice-date" />
                                </TableHead>
                                <TableHead className={HEAD_CLASS}>
                                    <FormattedMessage id="invoices.client-payer" />
                                </TableHead>
                                <TableHead className={HEAD_CLASS}>
                                    <FormattedMessage id="bank-transfers.invoice-remaining-amount" />
                                </TableHead>
                                <TableHead className={HEAD_CLASS} colSpan={2}>
                                    <FormattedMessage id="bank-transfers.payment-amount" />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {fields.map((field, index) => {
                                const watchedRow = bankTransferInvoicePaymentsWatched?.[index] ?? field;
                                const isCrossCurrency =
                                    field.invoicePaymentCurrencyTypeCode !==
                                    initialBankTransfer.entryAmountCurrencyTypeCode;
                                const conversion = isCrossCurrency
                                    ? getRowConversion(
                                          watchedRow,
                                          initialBankTransfer.entryAmountCurrencyTypeCode,
                                          conversionRates
                                      )
                                    : undefined;
                                const isEditable =
                                    (canUpdateAllBankTransactions ||
                                        (canUpdateMyCompanyBankTransactions &&
                                            employeeCompanyCountryId === watchedRow.companyCountryId)) &&
                                    !watchedRow.readonly;
                                const isTransactionCurrencyAmount = isAmountInTransactionCurrency(watchedRow);
                                const counterpartCurrencyTypeCode = isTransactionCurrencyAmount
                                    ? field.invoicePaymentCurrencyTypeCode
                                    : initialBankTransfer.entryAmountCurrencyTypeCode;
                                return (
                                    <TableRow key={field.id} className={SIMPLE_TABLE_ROW_CLASS}>
                                        <TableCell className={cn(CELL_CLASS, 'p-0')}>
                                            {/* The label names the checkbox after its invoice, for assistive tech. */}
                                            <label>
                                                <CheckboxInput
                                                    control={control}
                                                    name={`bankTransferInvoicePayments.${index}.isConnected`}
                                                    disabled={!isEditable}
                                                />
                                                <span className="sr-only">{field.invoiceNumber}</span>
                                            </label>
                                        </TableCell>
                                        <TableCell className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                                            {field.invoiceNumber}
                                        </TableCell>
                                        <TableCell className={CELL_CLASS}>
                                            {field.invoiceDate ? (
                                                <DateDefaultNowrap dateValue={field.invoiceDate} />
                                            ) : (
                                                ''
                                            )}
                                        </TableCell>
                                        <TableCell className={CELL_CLASS}>{field.clientPartyName}</TableCell>
                                        <TableCell className={CELL_CLASS}>
                                            <MoneyDisplay
                                                amount={field.outstandingBalanceInPaymentCurrency}
                                                currency={field.invoicePaymentCurrencyTypeCode}
                                            />
                                        </TableCell>
                                        <TableCell className={CELL_CLASS}>
                                            <DecimalInput
                                                gap={0}
                                                control={control}
                                                name={`bankTransferInvoicePayments.${index}.amount`}
                                                required
                                                inputProps={{ 'aria-label': t('bank-transfers.payment-amount') }}
                                                wrapperClassName="w-[120px]"
                                                disabled={!isEditable}
                                            />
                                        </TableCell>
                                        <TableCell className={CELL_CLASS}>
                                            <div className="flex items-center pr-2 whitespace-nowrap">
                                                {isCrossCurrency && conversion ? (
                                                    <Tooltip
                                                        content={
                                                            <FormattedMessage
                                                                id="bank-transfers.enter-amount-in"
                                                                values={{ currency: counterpartCurrencyTypeCode }}
                                                            />
                                                        }
                                                    >
                                                        <span>
                                                            <Button
                                                                {...buttonStyle(
                                                                    {
                                                                        variant: 'text',
                                                                        color: 'secondary',
                                                                        size: 'extraSmall',
                                                                    },
                                                                    'min-w-0 px-1'
                                                                )}
                                                                disabled={!isEditable}
                                                                onClick={() => {
                                                                    const switched = switchAmountCurrency(
                                                                        watchedRow,
                                                                        initialBankTransfer.entryAmountCurrencyTypeCode,
                                                                        conversion.rate
                                                                    );
                                                                    setValue(
                                                                        `bankTransferInvoicePayments.${index}.amountCurrencyTypeCode`,
                                                                        switched.amountCurrencyTypeCode
                                                                    );
                                                                    setValue(
                                                                        `bankTransferInvoicePayments.${index}.amount`,
                                                                        switched.amount
                                                                    );
                                                                }}
                                                            >
                                                                {watchedRow.amountCurrencyTypeCode}
                                                                <span className={ICON_SLOT.extraSmall}>
                                                                    <SwapHorizIcon fontSize="small" />
                                                                </span>
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    field.invoicePaymentCurrencyTypeCode
                                                )}
                                                {isCrossCurrency && conversion ? (
                                                    <span className="text-caption text-fg-secondary pl-1">
                                                        {'= '}
                                                        <MoneyDisplay
                                                            amount={
                                                                isTransactionCurrencyAmount
                                                                    ? toAmountInInvoiceCurrency(
                                                                          watchedRow,
                                                                          conversion.rate
                                                                      )
                                                                    : toAmountInTransactionCurrency(
                                                                          watchedRow,
                                                                          conversion.rate
                                                                      )
                                                            }
                                                            currency={counterpartCurrencyTypeCode}
                                                        />
                                                    </span>
                                                ) : null}
                                            </div>
                                            {isCrossCurrency && conversion ? (
                                                <div className="text-caption text-fg-secondary pr-2 whitespace-nowrap">
                                                    <FormattedMessage id="bank-transfers.conversion-rate" />{' '}
                                                    {formatConversionRate(conversion.rate)}
                                                    {conversion.date ? (
                                                        <>
                                                            {' ('}
                                                            <FormattedDate value={conversion.date} fallback="" />
                                                            {')'}
                                                        </>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {fields.length === 0 ? (
                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableCell className={cn(CELL_CLASS, 'text-center')} colSpan={7}>
                                        <FormattedMessage id="bank-transfers.no-connections" />
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </table>
                </div>
            </div>
            {canUpdateAllBankTransactions || canUpdateMyCompanyBankTransactions ? (
                <ModalFooter
                    onCancel={onClose}
                    onSave={() => {
                        void onSave();
                    }}
                    disableSave={disableSave}
                />
            ) : null}
        </>
    );
};

export default BankTransferConnectInvoices;
